/**
 * Renders an application's request template for one employee (ADR 0033).
 *
 * Shared, not server-only, so the setup page's live preview and the worker produce byte-identical
 * requests. Pure and RN-safe: no Date, no Node built-ins. Dates arrive as `YYYY-MM-DD` strings
 * already resolved in the right timezone by the caller (invariant 3).
 *
 * THE ONE RULE THAT MAKES THIS SAFE: a template is parsed JSON, and placeholders are replaced INSIDE
 * string values — never by pasting text into JSON. A name containing `"` or `}` therefore cannot
 * break the body or add a field. A string that is exactly one placeholder takes the value's own
 * type (a number stays a number, an empty value becomes `null`); a placeholder inside longer text is
 * converted to text.
 */

import {
  APP_INTEGRATION_PLACEHOLDER_SOURCE,
  parsePlaceholder,
  placeholdersIn,
  type AppIntegrationEmployeeField,
  type AppIntegrationOperationKind,
  type ParsedPlaceholder,
  type PlaceholderLocation,
} from './app-integrations.js';

export type RenderValue = string | number | boolean | null;

export interface RenderContext {
  /** Facts about the employee; `null` = known to be empty, missing key = not available. */
  readonly employee: Partial<Record<AppIntegrationEmployeeField, RenderValue>>;
  /** Custom fields the company marked "may be shared with applications", by key. */
  readonly custom: Readonly<Record<string, RenderValue>>;
  /** HR's answers to the application's questions, by key. */
  readonly inputs: Readonly<Record<string, RenderValue>>;
  /** The application's own id for this person, once known. */
  readonly externalId: string | null;
  /**
   * The key values sent to the application LAST time. In an update's path and query they replace
   * the current ones, so a corrected email still reaches the same account (N4).
   */
  readonly lastSent?: Partial<Record<'workEmail' | 'employeeCode' | 'id', string>>;
}

export interface RenderResult<T> {
  readonly value: T;
  /** Placeholders with no value available (`input.role` never answered): the send must wait. */
  readonly missing: readonly string[];
}

/** Marker for "drop this key / element" (the omitIfEmpty filter). */
const OMIT = Symbol('omit');
type Resolved = RenderValue | typeof OMIT;

function resolve(
  parsed: ParsedPlaceholder,
  ctx: RenderContext,
  where: PlaceholderLocation,
  operation: AppIntegrationOperationKind,
): RenderValue | undefined {
  switch (parsed.source) {
    case 'externalId':
      return ctx.externalId;
    case 'input':
      return ctx.inputs[parsed.key];
    case 'custom':
      return ctx.custom[parsed.key];
    case 'employee': {
      const key = parsed.key as AppIntegrationEmployeeField;
      const addressing = operation === 'update' && where !== 'body';
      if (addressing && (key === 'workEmail' || key === 'employeeCode' || key === 'id')) {
        const previous = ctx.lastSent?.[key];
        if (previous !== undefined) return previous;
      }
      return ctx.employee[key];
    }
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `YYYY-MM-DD` reformatted with the tokens YYYY, YY, MMM, MM, DD; anything else is left as is. */
export function formatTemplateDate(value: string, format: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const [, y, mo, d] = m;
  return format.replace(/YYYY|YY|MMM|MM|DD/g, (token) => {
    switch (token) {
      case 'YYYY':
        return y!;
      case 'YY':
        return y!.slice(2);
      case 'MMM':
        return MONTHS[Number(mo) - 1] ?? mo!;
      case 'MM':
        return mo!;
      default:
        return d!;
    }
  });
}

function applyFilters(value: RenderValue, parsed: ParsedPlaceholder): Resolved {
  let current: RenderValue = value;
  for (const filter of parsed.filters) {
    const empty = current === null || current === '';
    switch (filter.name) {
      case 'default':
        if (empty) current = filter.arg ?? '';
        break;
      case 'upper':
        if (typeof current === 'string') current = current.toUpperCase();
        break;
      case 'lower':
        if (typeof current === 'string') current = current.toLowerCase();
        break;
      case 'date':
        if (typeof current === 'string' && filter.arg)
          current = formatTemplateDate(current, filter.arg);
        break;
      case 'omitIfEmpty':
        if (empty) return OMIT;
        break;
    }
  }
  return current;
}

function renderString(
  text: string,
  ctx: RenderContext,
  where: PlaceholderLocation,
  operation: AppIntegrationOperationKind,
  missing: Set<string>,
): Resolved {
  const found = placeholdersIn(text);
  if (found.length === 0) return text;

  const valueOf = (p: (typeof found)[number]): Resolved => {
    const parsed = parsePlaceholder(p.name, p.filters);
    // Templates are validated on save; an invalid one here renders as empty rather than leaking.
    if ('problem' in parsed) return null;
    const raw = resolve(parsed, ctx, where, operation);
    if (raw === undefined) {
      missing.add(p.name);
      return null;
    }
    return applyFilters(raw, parsed);
  };

  // Exactly one placeholder and nothing else: keep the value's own type.
  if (found.length === 1 && text.trim() === found[0]!.raw) {
    const single = valueOf(found[0]!);
    if (single === OMIT || where === 'body') return single;
    return single === null ? '' : encodeIfPath(String(single), where);
  }

  let omit = false;
  const pattern = new RegExp(APP_INTEGRATION_PLACEHOLDER_SOURCE, 'g');
  const out = text.replace(pattern, (raw, name: string, filters: string) => {
    const v = valueOf({ name: name.trim(), filters: filters ?? '', raw });
    if (v === OMIT) {
      omit = true;
      return '';
    }
    return v === null ? '' : encodeIfPath(String(v), where);
  });
  return omit && out.trim() === '' ? OMIT : out;
}

/** In a path every substituted value is percent-encoded, so it cannot add a segment or a query. */
function encodeIfPath(value: string, where: PlaceholderLocation): string {
  return where === 'path' ? encodeURIComponent(value) : value;
}

function renderNode(
  node: unknown,
  ctx: RenderContext,
  where: PlaceholderLocation,
  operation: AppIntegrationOperationKind,
  missing: Set<string>,
  depth: number,
): Resolved | unknown {
  if (depth > 20) return null;
  if (typeof node === 'string') return renderString(node, ctx, where, operation, missing);
  if (Array.isArray(node)) {
    return node
      .map((v) => renderNode(v, ctx, where, operation, missing, depth + 1))
      .filter((v) => v !== OMIT);
  }
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(node)) {
      const rendered = renderNode(v, ctx, where, operation, missing, depth + 1);
      if (rendered !== OMIT) out[key] = rendered;
    }
    return out;
  }
  return node;
}

/** Renders a body (any JSON value). */
export function renderTemplateBody(
  template: unknown,
  ctx: RenderContext,
  operation: AppIntegrationOperationKind,
): RenderResult<unknown> {
  const missing = new Set<string>();
  const value = renderNode(template, ctx, 'body', operation, missing, 0);
  return { value: value === OMIT ? null : value, missing: [...missing] };
}

/** Renders a path ("/users/{{externalId}}"); substituted values are percent-encoded. */
export function renderTemplatePath(
  template: string,
  ctx: RenderContext,
  operation: AppIntegrationOperationKind,
): RenderResult<string> {
  const missing = new Set<string>();
  const value = renderString(template, ctx, 'path', operation, missing);
  return { value: value === OMIT || value === null ? '' : String(value), missing: [...missing] };
}

/** Renders query parameters; a parameter whose value is omitted is dropped. */
export function renderTemplateQuery(
  template: Readonly<Record<string, string>>,
  ctx: RenderContext,
  operation: AppIntegrationOperationKind,
): RenderResult<Record<string, string>> {
  const missing = new Set<string>();
  const value: Record<string, string> = {};
  for (const [key, text] of Object.entries(template)) {
    const rendered = renderString(text, ctx, 'query', operation, missing);
    if (rendered !== OMIT) value[key] = rendered === null ? '' : String(rendered);
  }
  return { value, missing: [...missing] };
}

/** The coarse status applications may see (N2): never notice period, leave or exit. */
export function appIntegrationStatusOf(employmentStatus: string): 'pre_joining' | 'active' {
  return employmentStatus === 'pre_joining' ? 'pre_joining' : 'active';
}
